import AppContext from 'App/AppContext';
import { IPCheckbox, IPDefaultButton, IPPrimaryButton, IPSeparator, IPText } from 'baseComponents';
import { IPPanel, IPPanelType } from 'baseComponents/Panel/IPPanel';
import { IPStack } from 'baseComponents/Stack/IPStack';
import { IPTextField } from 'baseComponents/TextField/IPTextField';
import { IPeriodFilterData } from 'components/Filter/PeriodFilter';
import {
  globalLayerRightSideBarProps,
  globalStackStylesPanel,
  globalStackTokensGapMedium,
  globalStackTokensGapSmall,
  globalTextStylesBold,
} from 'globalStyles';
import Widget, { getWidgetTypes, IWidgetType, Widget_Translation, WidgetSizes } from 'models/widget';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import WidgetConfigRenderer from './WidgetConfigRenderer';
import WidgetSizeSelector from './WidgetSizeSelector';

interface IEditWidgetPanelProps {
  widget: Widget;
  isOpen: boolean;
  period?: IPeriodFilterData;
  onEdit: (widget: Widget) => void;
  onClose: () => void;
}

const EditWidgetPanel = (props: IEditWidgetPanelProps) => {
  const appContext = useContext(AppContext);
  const { t } = useTranslation(['widgets', 'translation', 'dashboard']);
  const [configuredWidget, setConfiguredWidget] = useState<Widget>(props.widget.clone());
  const [configError, setConfigError] = useState<boolean>(false);
  const [overWriteName, setOverWriteName] = useState<boolean>(false);
  const [widgetName, setWidgetName] = useState<string | undefined>(undefined);
  const [widgetTypes] = useState<IWidgetType[]>(getWidgetTypes(appContext, t));

  useEffect(() => {
    if (props.widget.name) {
      setWidgetName(props.widget.name);
      setOverWriteName(true);
    }
  }, [props]);

  const onUpdateConfig = (config: string, isValid: boolean) => {
    if (!configuredWidget) return;

    if (isValid) {
      const newWidget = configuredWidget.clone();
      newWidget.widgetConfig = config;
      setConfiguredWidget(newWidget);
    }

    setConfigError(!isValid);
  };

  const onEditWidget = () => {
    props.onEdit(configuredWidget);
  };

  const onSetWidgetName = (value: string | undefined) => {
    if (!configuredWidget) return;
    const widgetClone = configuredWidget.clone();
    widgetClone.name = value?.trimStart() ?? '';
    setWidgetName(widgetClone.name);

    if (value) {
      // If the languageId is already set (because it was returned by the backend) use it.
      let languageId = appContext.user.language.languageId;
      if (widgetClone.trans && widgetClone.trans.length > 0) {
        languageId = widgetClone.trans[0].languageId;
      }

      const translation = new Widget_Translation();

      translation.languageId = languageId;

      translation.lang = appContext.user.language;
      translation.name = widgetClone.name;
      widgetClone.trans = [translation];
      widgetClone.transIdx = 0;
    } else {
      // If the name is empty, clear the translations.
      widgetClone.trans = [];
    }

    setConfiguredWidget(widgetClone);
  };

  // When the checkbox is toggled, set the widget name to the cached value or clear it.
  const onSetOverwriteName = (value: boolean) => {
    onSetWidgetName(value ? widgetName : undefined);
    setOverWriteName(value);
  };

  const onSetSize = (size: WidgetSizes) => {
    if (!configuredWidget) return;
    const widgetClone = configuredWidget.clone();
    widgetClone.widgetSize = size;
    setConfiguredWidget(widgetClone);
  };

  const hasError = (): boolean => {
    return configError;
  };

  const getTypeName = (): string => {
    const type = widgetTypes.find((w) => w.type === configuredWidget.widgetType);

    return type?.name ?? '';
  };

  const getTypeDescription = (): string => {
    const type = widgetTypes.find((w) => w.type === configuredWidget.widgetType);

    return type?.description ?? '';
  };

  //
  // Render
  //

  const onRenderFooterContentConfiguredWidget = () => {
    return (
      <IPStack horizontal styles={globalStackStylesPanel} tokens={globalStackTokensGapSmall}>
        <IPStack.Item>
          <IPPrimaryButton onClick={() => onEditWidget()} disabled={hasError()}>
            {t('translation:General.Button.Save')}
          </IPPrimaryButton>
        </IPStack.Item>
        <IPStack.Item>
          <IPDefaultButton onClick={props.onClose}>{t('translation:General.Button.Cancel')}</IPDefaultButton>
        </IPStack.Item>
      </IPStack>
    );
  };

  return (
    <IPPanel
      headerText={t('dashboard:EditWidgetPanel.Title')}
      type={IPPanelType.medium}
      isBlocking={true}
      isHiddenOnDismiss={false}
      isOpen={props.isOpen}
      onDismiss={props.onClose}
      isFooterAtBottom={true}
      layerProps={globalLayerRightSideBarProps}
      onRenderFooterContent={onRenderFooterContentConfiguredWidget}
    >
      <IPStack verticalFill styles={globalStackStylesPanel} tokens={globalStackTokensGapMedium}>
        <IPStack.Item>
          <IPStack horizontal verticalAlign="start">
            <IPStack.Item>
              <IPStack>
                <IPText variant="large" styles={globalTextStylesBold}>
                  {getTypeName()}
                </IPText>
                {!appContext.isMobileView && <IPText> {getTypeDescription()}</IPText>}
              </IPStack>
            </IPStack.Item>
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            <IPStack.Item grow>
              <IPTextField
                label={t('dashboard:EditWidgetPanel.NameLabel')}
                autoFocus={!appContext.isMobileView}
                value={configuredWidget.name}
                placeholder={configuredWidget.getName(appContext, t)}
                onChange={(ev, newValue) => onSetWidgetName(newValue)}
                maxLength={128}
                disabled={!overWriteName}
              />
            </IPStack.Item>
            {configuredWidget.getType(widgetTypes)?.canAdjustSize && (
              <IPStack.Item>
                <WidgetSizeSelector widget={configuredWidget} onChange={onSetSize} />
              </IPStack.Item>
            )}
          </IPStack>
        </IPStack.Item>
        <IPStack.Item>
          <IPCheckbox
            checked={overWriteName}
            label={t('dashboard:AddWidgetPanel.OverwriteNameLabel')}
            onChange={(ev, checked) => {
              onSetOverwriteName(checked ?? false);
            }}
          />
          <IPSeparator />
        </IPStack.Item>
        <IPStack.Item grow>
          <WidgetConfigRenderer
            widget={configuredWidget}
            onUpdateConfig={onUpdateConfig}
            editmode={true}
            period={props.period}
          />
        </IPStack.Item>
      </IPStack>
    </IPPanel>
  );
};

export default EditWidgetPanel;
